import type { ReactElement, ReactNode } from 'react';
import styles from './DataTable.module.scss';

export interface DataColumn<T> {
  key: string;
  /** Заголовок колонки; на узком экране — подпись значения в карточке. */
  header: ReactNode;
  cell(row: T): ReactNode;
  /** Главная колонка: на узком экране — заголовок карточки, без подписи. */
  primary?: boolean;
  /** Колонка действий: прижата вправо, на узком экране — в подвале карточки без подписи. */
  actions?: boolean;
  align?: 'start' | 'end';
}

export interface DataTableProps<T> {
  /** Подпись таблицы для экранных читалок. */
  label: string;
  columns: readonly DataColumn<T>[];
  rows: readonly T[];
  rowKey(row: T): string;
  /** Показывается вместо таблицы, если строк нет. */
  empty?: ReactNode;
}

/**
 * Таблица, которая на узком экране (до 640 px) показывает каждую строку карточкой «подпись —
 * значение» той же разметкой: подписи берутся из data-label (ADR-011, SYS-UC-11).
 */
export function DataTable<T>({ label, columns, rows, rowKey, empty }: DataTableProps<T>): ReactElement {
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className={styles.wrap}>
      <table className={styles.table} aria-label={label}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" className={cellClass(column)}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td key={column.key} className={cellClass(column)} data-label={typeof column.header === 'string' && !column.primary && !column.actions ? column.header : undefined}>
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function cellClass<T>(column: DataColumn<T>): string {
  return [column.primary ? styles.primary : '', column.actions ? styles.actions : '', column.align === 'end' ? styles.end : ''].filter(Boolean).join(' ');
}
